import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { setFlashToast } from '../../../utils/flashToast';
import * as criminalService from '../../../services/criminalCaseRequests';
import { getProfile, type ExtranetUserProfile } from '../../../services/userProfile';
import { calcAgeFromDob } from '../../../utils/caseFieldValidation';
import { focusFirstInvalid, validateRequired, type RequiredErrors, type RequiredFieldSpec } from '../../../utils/requiredValidation';
import { useCriminalWizardContext } from '../../../hooks/useCaseRequestWizard';
import { useCriminalLookups } from './useCriminalLookups';
import { formatDateRange, formatDisplayDate } from '../../../utils/dateFormat';
import { IconWarningTriangle } from '../../../components/icons/WizardIcons';
import { CriminalReviewDocumentsTable } from './CriminalReviewDocumentsTable';
import { CriminalWizardActions } from './CriminalWizardActions';
import { apiOrLookupLabel, deliveryMethodDisplay } from '../../view-request/viewRequestHelpers';
import { isBriberySubCategory, isOtherLookup } from './criminalConstants';
import {
  ReviewChips,
  ReviewField,
  ReviewGrid,
  ReviewListField,
  ReviewSection,
  ReviewSubBlock,
} from './CriminalReviewUi';
import CriminalCoverNoteModal from './CriminalCoverNoteModal';
import { criminalWizardPath } from './CriminalWizardShell';
import {
  wizardField,
  wizardReviewStepLabel,
  wizardSection,
  wizardStepTitle,
  wizardYesNo,
} from '../wizardI18nHelpers';
import { useRole } from '../../../rbac/hooks';
import { tasksListPathForRole } from '../../../rbac/tasksListPath';

function labelFor(id: string, items: { id: string; name: string }[]) {
  return items.find((x) => x.id === id)?.name ?? (id || '—');
}

function accusedLabel(
  name: string,
  statusId: string,
  statusOther: string,
  statuses: { id: string; name: string }[],
) {
  const statusName =
    statusId === 'other' ? statusOther : statuses.find((s) => s.id === statusId)?.name ?? statusId;
  if (!name.trim()) return '—';
  return statusName ? `${name.trim()} (${statusName})` : name.trim();
}

export default function CriminalReviewPage() {
  const nav = useNavigate();
  const { t } = useTranslation();
  const { roleCode } = useRole();
  const tasksListPath = tasksListPathForRole(roleCode);
  // const { user } = useAuth();
  const { draft, setDraft } = useCriminalWizardContext();
  const [errors, setErrors] = useState<RequiredErrors>({});
  const [profile, setProfile] = useState<ExtranetUserProfile | null>(null);
  const [showCoverNote, setShowCoverNote] = useState(false);
  const [coverNoteRequestId, setCoverNoteRequestId] = useState('');
  const [coverNoteFileRef, setCoverNoteFileRef] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const lookups = useCriminalLookups('review', draft.provinceId, draft.districtId);

  useEffect(() => {
    let mounted = true;
    void getProfile().then((r) => {
      if (mounted && r.success) setProfile(r.data);
    });
    return () => {
      mounted = false;
    };
  }, []);

  const subName = draft.subFileCategoryName || '—';
  const showHighCourt = useMemo(
    () => isBriberySubCategory(draft.subFileCategoryName || ''),
    [draft.subFileCategoryName],
  );
  const victimAge = useMemo(() => calcAgeFromDob(draft.victimDob), [draft.victimDob]);
  const under16 = typeof victimAge === 'number' && victimAge < 16;

  const caseSourceLabel = useMemo(() => {
    const item = lookups.caseSources.find((s) => s.id === draft.caseSourceId);
    if (item && isOtherLookup(item.name, item.code)) return draft.caseSourceOther || item.name;
    return labelFor(draft.caseSourceId, lookups.caseSources) || draft.caseSource;
  }, [draft, lookups.caseSources]);

  const deliveryLabel = useMemo(
    () => deliveryMethodDisplay(draft, lookups.deliveryMethods),
    [draft, lookups.deliveryMethods],
  );

  const coverNoteFallback = useMemo(
    () => ({
      fileReferenceNumber: coverNoteFileRef || draft.referenceNo,
      submittedDivisionName: profile?.divisionUnit?.trim() || profile?.externalAgencyName?.trim() || '',
    }),
    [coverNoteFileRef, draft.referenceNo, profile?.divisionUnit, profile?.externalAgencyName],
  );

  const connectedLabel = wizardYesNo(t, draft.hasConnectedMatters);

  const submit = async () => {
    if (submitting) return;

    const specs: RequiredFieldSpec[] = [
      { key: 'confirmCorrect', label: 'Confirmation', value: draft.confirmCorrect ? 'yes' : '', elementId: 'crim-confirm' },
    ];
    const reqErrs = validateRequired(specs);
    setErrors(reqErrs);
    if (Object.keys(reqErrs).length) {
      focusFirstInvalid(specs, reqErrs);
      return;
    }

    setSubmitting(true);
    const r = await criminalService.saveCriminalRequest(draft, {
      isDraft: false,
      allSteps: true,
      confirmedCorrect: true,
    });
    if (!r.success) {
      setSubmitting(false);
      setFlashToast({ message: r.message ?? t('wizard.nav.submitFailed'), variant: 'error' });
      return;
    }
    setDraft((d) => ({
      ...d,
      draftRequestId: r.data.requestId,
      referenceNo: r.data.fileReferenceNumber ?? d.referenceNo,
    }));
    setFlashToast({ message: t('wizard.nav.submitSuccess'), variant: 'success' });
    setCoverNoteRequestId(r.data.requestId);
    setCoverNoteFileRef(r.data.fileReferenceNumber ?? draft.referenceNo ?? '');
    setShowCoverNote(true);
  };

  const closeCoverNote = () => {
    setShowCoverNote(false);
    setCoverNoteRequestId('');
    setCoverNoteFileRef('');
    nav(tasksListPath);
  };

  const reviewCrumbTo = criminalWizardPath(1, draft.draftRequestId);
  const editTo = (step: 1 | 2 | 3 | 4 | 5) => criminalWizardPath(step, draft.draftRequestId);

  return (
    <>
      <CriminalCoverNoteModal
        isOpen={showCoverNote}
        caseRequestId={coverNoteRequestId}
        fileRefFallback={coverNoteFileRef}
        coverNoteFallback={coverNoteFallback}
        showSuccessBanner
        onClose={closeCoverNote}
      />
      <div className="ccr-review-page">
      <div className="bc">
        <Link to={tasksListPath}>{t('nav.tasksAndCases')}</Link>
        <span className="bc-sep">/</span>
        <Link to={reviewCrumbTo}>{t('wizard.createCaseRequest')}</Link>
        <span className="bc-sep">/</span>
        {t('wizard.reviewBeforeSubmission')}
      </div>
      <div className="pg-title">{t('wizard.reviewTitle')}</div>

      <ReviewSection step={wizardReviewStepLabel(t, 1)} title={wizardStepTitle(t, 'criminal', 1)} editTo={editTo(1)}>
        <ReviewSubBlock title={wizardSection(t, 'caseIntakeDetails')}>
          <ReviewGrid cols={4}>
            <ReviewField label={wizardField(t, 'fileCategory')} value={draft.fileCategory} />
            <ReviewField label={wizardField(t, 'subFileCategory')} value={subName} />
          </ReviewGrid>
          <ReviewChips items={draft.applicableAreas} />
          <ReviewField label={wizardField(t, 'description')} value={draft.description} fullWidth />
          <ReviewGrid cols={4}>
            <ReviewField label={wizardField(t, 'caseSource')} value={caseSourceLabel} />
            <ReviewField
              label={wizardField(t, 'province')}
              value={apiOrLookupLabel(draft.provinceName, draft.provinceId, lookups.provinces)}
            />
            <ReviewField
              label={wizardField(t, 'district')}
              value={apiOrLookupLabel(draft.districtName, draft.districtId, lookups.districts)}
            />
            {showHighCourt ? (
              <ReviewField
                label={wizardField(t, 'highCourtName')}
                value={apiOrLookupLabel(draft.highCourtName, draft.highCourtId, lookups.highCourts)}
              />
            ) : null}
            <ReviewField
              label={wizardField(t, 'magistrateCourtName')}
              value={apiOrLookupLabel(draft.magistrateCourtName, draft.magistrateCourtId, lookups.magistrateCourts)}
            />
            <ReviewField
              label={wizardField(t, 'policeStation')}
              value={apiOrLookupLabel(draft.policeStationName, draft.policeStationId, lookups.policeStations)}
            />
            <ReviewField label={wizardField(t, 'yourReferenceNumber')} value={draft.yourReferenceNumber} />
            <ReviewField label={wizardField(t, 'otherExternalReferences')} value={draft.otherExternalReferences} />
            <ReviewField label={wizardField(t, 'caseNumber')} value={draft.caseNumber} />
            <ReviewField label={wizardField(t, 'connectedMatters')} value={connectedLabel} />
            <ReviewField label={wizardField(t, 'connectedCaseNumber')} value={draft.connectedCaseNumber} />
            <ReviewField label={wizardField(t, 'connectedAgdFileRef')} value={draft.connectedAgdFileRef} />
          </ReviewGrid>
        </ReviewSubBlock>
      </ReviewSection>

      <ReviewSection step={wizardReviewStepLabel(t, 2)} title={wizardStepTitle(t, 'criminal', 2)} editTo={editTo(2)}>
        <ReviewSubBlock title={wizardSection(t, 'victimDetails')}>
          <ReviewGrid cols={4}>
            <ReviewField label={wizardField(t, 'victimName')} value={draft.victimName} />
            <ReviewField label={wizardField(t, 'dateOfBirth')} value={formatDisplayDate(draft.victimDob)} />
            <div className="ccr-review-field">
              <div className="ccr-review-lbl">{wizardField(t, 'age')}</div>
              <div className="ccr-review-age-row">
                <span className="ccr-review-val">{typeof victimAge === 'number' ? String(victimAge) : '—'}</span>
                {under16 ? (
                  <span className="ccr-review-badge">
                    <IconWarningTriangle />
                    {t('wizard.childUnder16')}
                  </span>
                ) : null}
              </div>
            </div>
            <ReviewField label={wizardField(t, 'gender')} value={labelFor(draft.victimGenderId, lookups.genders) || draft.victimGender} />
          </ReviewGrid>
        </ReviewSubBlock>
      </ReviewSection>

      <ReviewSection step={wizardReviewStepLabel(t, 3)} title={wizardStepTitle(t, 'criminal', 3)} editTo={editTo(3)}>
        <ReviewSubBlock title={wizardSection(t, 'offenceDetails')}>
          <ReviewGrid cols={3}>
            <ReviewField label={wizardField(t, 'dateOfOffence')} value={formatDateRange(draft.offenceDateFrom, draft.offenceDateTo)} />
            <ReviewField label={wizardField(t, 'placeOfOffence')} value={draft.placeOfOffence} />
            <ReviewField
              label={wizardField(t, 'accusedDetailsAvailable')}
              value={draft.accusedDetailsAvailable ? t('common.yes') : t('common.no')}
            />
            {draft.accusedDetailsAvailable ? (
              <ReviewListField
                label={wizardField(t, 'accusedSuspects')}
                items={draft.accused.map((a) =>
                  accusedLabel(a.name, a.status, a.statusOther, lookups.suspectStatuses),
                )}
              />
            ) : null}
          </ReviewGrid>
        </ReviewSubBlock>
        <ReviewSubBlock title={wizardSection(t, 'timeline')}>
          <ReviewGrid cols={3}>
            <ReviewField
              label={wizardField(t, 'urgencyLevel')}
              value={apiOrLookupLabel(draft.urgencyLevelName, draft.urgencyLevelId, lookups.urgencyLevels)}
            />
            <ReviewField label={wizardField(t, 'deadline')} value={formatDisplayDate(draft.deadline)} />
            <ReviewField label={wizardField(t, 'urgencyJustification')} value={draft.urgencyJustification} />
          </ReviewGrid>
        </ReviewSubBlock>
      </ReviewSection>

      <ReviewSection step={wizardReviewStepLabel(t, 4)} title={wizardStepTitle(t, 'criminal', 4)} editTo={editTo(4)}>
        <ReviewSubBlock title={wizardSection(t, 'requestSubmitterDetails')}>
          <ReviewGrid cols={4}>
            <ReviewField label={wizardField(t, 'submittedDivisionUnit')} value={profile?.divisionUnit?.trim() || profile?.externalAgencyName?.trim() || '—'} />
            <ReviewField label={wizardField(t, 'designation')} value={profile?.contactDesignation?.trim() || '—'} />
            <ReviewField label={wizardField(t, 'submittedBy')} value={profile?.displayName?.trim() || '—'} />
            <span className="ccr-review-grid-spacer" aria-hidden="true" />
            <ReviewField label={wizardField(t, 'emailAddress')} value={profile?.email?.trim() || '—'} />
            <ReviewField label={wizardField(t, 'contactNumber')} value={profile?.contactNumber?.trim() || '—'} />
          </ReviewGrid>
        </ReviewSubBlock>
        <ReviewSubBlock title={wizardSection(t, 'physicalFileDeliveryMethod')}>
          <ReviewField label={wizardField(t, 'physicalFileDeliveryMethod')} value={deliveryLabel} fullWidth />
        </ReviewSubBlock>
      </ReviewSection>

      <ReviewSection step={wizardReviewStepLabel(t, 5)} title={wizardStepTitle(t, 'criminal', 5)} editTo={editTo(5)}>
        <ReviewSubBlock>
          <CriminalReviewDocumentsTable documents={draft.documents} />
        </ReviewSubBlock>
      </ReviewSection>

      <div className="confirm-section ccr-review-confirm">
        <label className="ccr-chk" id="crim-confirm">
          <input
            type="checkbox"
            checked={draft.confirmCorrect}
            disabled={submitting}
            onChange={(e) => {
              setDraft((d) => ({ ...d, confirmCorrect: e.target.checked }));
              if (e.target.checked) setErrors((er) => ({ ...er, confirmCorrect: '' }));
            }}
          />
          <span className="ccr-review-confirm-text">
            <span className="ccr-review-confirm-main">
              {t('wizard.confirm.main')} <span className="req">*</span>
            </span>
            <span className="ccr-review-confirm-hint">{t('wizard.confirm.hint')}</span>
          </span>
        </label>
        {errors.confirmCorrect ? <div className="ccr-review-confirm-err">{errors.confirmCorrect}</div> : null}
      </div>

      <CriminalWizardActions
        draft={draft}
        setDraft={setDraft}
        currentStep={5}
        onBack={() => nav(editTo(5))}
        showSubmit
        submitting={submitting}
        onSubmit={() => void submit()}
        reviewMode
      />
    </div>
    </>
  );
}
